import util from '../lib/util';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
function getCurrentUserId() { return sessionStorage.getItem('userId'); }
function getCurrentFirmId() { return sessionStorage.getItem('firmId'); }
export const TriggerType = {
    CaseCreated: "C",
    StageEntered: "S",
    CustomDate: "D",
    RuleCompleted: "R"
};
export type TriggerTypeType = typeof TriggerType[keyof typeof TriggerType];
export const OffsetUnit = {
    CalendarDays: "C",
    BusinessDays: "B"
};
export type OffsetUnitType = typeof OffsetUnit[keyof typeof OffsetUnit];
export const RolloverDirection = {
    Forward: "F",
    Backward: "B",
    NextWeekday: "W"
};
export type RolloverDirectionType = typeof RolloverDirection[keyof typeof RolloverDirection];
export const ServiceExtensionUnit = {
    CalendarDays: "C",
    BusinessDays: "B"
};
export type ServiceExtensionUnitType = typeof ServiceExtensionUnit[keyof typeof ServiceExtensionUnit];
export const Priority = {
    Low: "L",
    Medium: "M",
    High: "H"
};
export type PriorityType = typeof Priority[keyof typeof Priority];
class DeadlineRule extends Base implements EntityType {
    deadlineRuleId: uuid;
    caseTypeId?: uuid | null;
    jurisdiction?: string | null;
    ruleReference?: string | null;
    name: string;
    description?: string | null;
    triggerType: TriggerTypeType;
    triggerStageId?: uuid | null;
    triggerRuleId?: uuid | null;
    triggerDateLabel?: string | null;
    offsetAmount: number;
    offsetUnit: OffsetUnitType;
    rolloverDirection: RolloverDirectionType;
    rolloverWeekday?: number | null;
    serviceExtensionDays?: number | null;
    serviceExtensionUnit?: ServiceExtensionUnitType | null;
    serviceExtensionLabel?: string | null;
    priority: PriorityType;
    sortOrder: number;
    autoFire: boolean;
    color?: string | null;
    reminderDaysBefore?: number | null;
    isCommon: boolean;
    inclusiveStart: boolean;
    inclusiveEnd: boolean;
    constructor(doc: typeof DeadlineRule | any) {
        super(doc);
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: "C",
            entityId: null,
            entityType: null,
            deadlineRuleId: genUUID(),
            name: null,
            triggerType: null,
            offsetAmount: null,
            offsetUnit: null,
            sortOrder: null,
        };
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || "deadlineRule";
        // @ts-ignore
        this.deadlineRuleId = doc._deadlineRuleId || doc.deadlineRuleId || doc.deadlineruleid || genUUID();
        // @ts-ignore
        this.caseTypeId = doc._caseTypeId || doc.caseTypeId || doc.casetypeid || null;
        // @ts-ignore
        this.jurisdiction = doc._jurisdiction || doc.jurisdiction || null;
        // @ts-ignore
        this.ruleReference = doc._ruleReference || doc.ruleReference || doc.rulereference || null;
        // @ts-ignore
        this.name = doc._name || doc.name || null;
        // @ts-ignore
        this.description = doc._description || doc.description || null;
        // @ts-ignore
        this.triggerType = util.trimRight(doc._triggerType) || util.trimRight(doc.triggerType) || util.trimRight(doc.triggertype) || null;
        // @ts-ignore
        this.triggerStageId = doc._triggerStageId || doc.triggerStageId || doc.triggerstageid || null;
        // @ts-ignore
        this.triggerRuleId = doc._triggerRuleId || doc.triggerRuleId || doc.triggerruleid || null;
        // @ts-ignore
        this.triggerDateLabel = doc._triggerDateLabel || doc.triggerDateLabel || doc.triggerdatelabel || null;
        // @ts-ignore
        this.offsetAmount = util.coalesce(util.parseInt(doc._offsetAmount), util.parseInt(doc.offsetAmount), util.parseInt(doc.offsetamount), null);
        // @ts-ignore
        this.offsetUnit = util.trimRight(doc._offsetUnit) || util.trimRight(doc.offsetUnit) || util.trimRight(doc.offsetunit) || null;
        // @ts-ignore
        this.rolloverDirection = util.trimRight(doc._rolloverDirection) || util.trimRight(doc.rolloverDirection) || util.trimRight(doc.rolloverdirection) || "F";
        // @ts-ignore
        this.rolloverWeekday = util.coalesce(util.parseInt(doc._rolloverWeekday), util.parseInt(doc.rolloverWeekday), util.parseInt(doc.rolloverweekday), null);
        // @ts-ignore
        this.serviceExtensionDays = util.coalesce(util.parseInt(doc._serviceExtensionDays), util.parseInt(doc.serviceExtensionDays), util.parseInt(doc.serviceextensiondays), null);
        // @ts-ignore
        this.serviceExtensionUnit = util.trimRight(doc._serviceExtensionUnit) || util.trimRight(doc.serviceExtensionUnit) || util.trimRight(doc.serviceextensionunit) || null;
        // @ts-ignore
        this.serviceExtensionLabel = doc._serviceExtensionLabel || doc.serviceExtensionLabel || doc.serviceextensionlabel || null;
        // @ts-ignore
        this.priority = util.trimRight(doc._priority) || util.trimRight(doc.priority) || "M";
        // @ts-ignore
        this.sortOrder = util.coalesce(util.parseInt(doc._sortOrder), util.parseInt(doc.sortOrder), util.parseInt(doc.sortorder), null);
        // @ts-ignore
        this.autoFire = util.defaultBoolean([doc.autoFire, doc._autoFire, doc.autofire, true]);
        // @ts-ignore
        this.color = doc._color || doc.color || null;
        // @ts-ignore
        this.reminderDaysBefore = util.coalesce(util.parseInt(doc._reminderDaysBefore), util.parseInt(doc.reminderDaysBefore), util.parseInt(doc.reminderdaysbefore), null);
        // @ts-ignore
        this.isCommon = util.defaultBoolean([doc.isCommon, doc._isCommon, doc.iscommon, false]);
        // @ts-ignore
        this.inclusiveStart = util.defaultBoolean([doc.inclusiveStart, doc._inclusiveStart, doc.inclusivestart, false]);
        // @ts-ignore
        this.inclusiveEnd = util.defaultBoolean([doc.inclusiveEnd, doc._inclusiveEnd, doc.inclusiveend, true]);
    }
    get id(): uuid { return this.deadlineRuleId; }
    static TRIGGER_TYPE_VALUES = ["C", "S", "D", "R"];
    static OFFSET_UNIT_VALUES = ["C", "B"];
    static ROLLOVER_DIRECTION_VALUES = ["F", "B", "W"];
    static SERVICE_EXTENSION_UNIT_VALUES = ["C", "B"];
    static PRIORITY_VALUES = ["L", "M", "H"];
    static override getPkFields(): SchemaField[] { return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique id for this deadline rule"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","comment":"Associated firm"}]; }
    toLowerCase() { return this.deadlineRuleId; }
    static override getNonRefArrayFields(): string[] { return []; }
    static override getDefaultEntries(): DeadlineRule[] {
        return [];
    }
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
    entity: 'deadlineRule',
    dataArea: 'Firm Settings',
    table: 'DEADLINE_RULE',
    comment: 'A deadline rule is a reusable template that defines how a case deadline is calculated and triggered. When a case is created, advances to a stage, reaches a custom date, or another rule completes, matching rules automatically generate Deadline instances with dates computed from a configurable offset (calendar or business days), weekend/holiday rollover, and optional service extensions. Rules may be scoped to a specific case type or applied at the jurisdiction level.',
    fields: {
        deadlineRuleId: {
            type: 'uuid',
            required: true,
            pk: true,
            default: '__UUID__',
            comment: 'Unique id for this deadline rule'
        },

        /**
         * The case type this rule belongs to (optional for jurisdiction-level rules)
         */
        caseTypeId: {
            type: 'uuid',
            entityType: 'caseType',
            comment: 'The case type this rule belongs to (null for jurisdiction rules)'
        },

        /**
         * Jurisdiction code, e.g. "Federal", "TX", "CA"
         * Links rules to courts via court metadata
         */
        jurisdiction: {
            type: 'string',
            searchable: true,
            comment: 'Jurisdiction code (e.g. Federal, TX, CA)'
        },

        /**
         * Court rule citation, e.g. "FRCP 12(a)(1)"
         */
        ruleReference: {
            type: 'string',
            searchable: true,
            comment: 'Court rule citation (e.g. FRCP 12(a)(1))'
        },

        /**
         * Display name, e.g. "Answer Due", "Discovery Cutoff", "Motion Deadline"
         */
        name: {
            type: 'string',
            required: true,
            searchable: true,
            comment: 'Display name for this deadline rule'
        },

        /**
         * Detailed description of the rule
         */
        description: {
            type: 'string',
            comment: 'Description of what this deadline is for'
        },

        /**
         * What event triggers this deadline:
         * [C]ase Created - fires when a new case of this type is created
         * [S]tage Entered - fires when a specific stage becomes active
         * [D]ate - fires from a custom date field (user-entered trigger date)
         * [R]ule Completed - fires when another deadline rule's deadline is completed (chaining)
         */
        triggerType: {
            type: 'char',
            required: true,
            values: ['C', 'S', 'D', 'R'],
            valueNames: ['Case Created', 'Stage Entered', 'Custom Date', 'Rule Completed'],
            comment: 'What triggers this deadline: [C]ase Created, [S]tage Entered, [D]ate, [R]ule Completed'
        },

        /**
         * If triggerType=S, which CaseTypeStage triggers this rule
         */
        triggerStageId: {
            type: 'uuid',
            entityType: 'caseTypeStage',
            comment: 'If trigger is Stage Entered, which stage'
        },

        /**
         * If triggerType=R, which DeadlineRule must complete first (chaining)
         */
        triggerRuleId: {
            type: 'uuid',
            entityType: 'deadlineRule',
            comment: 'If trigger is Rule Completed, which rule'
        },

        /**
         * If triggerType=D, a label for the custom date field, e.g. "Service Date"
         */
        triggerDateLabel: {
            type: 'string',
            comment: 'Label for the custom trigger date field'
        },

        /**
         * Number of units to offset from the trigger. Positive = after, negative = before.
         */
        offsetAmount: {
            type: 'int',
            required: true,
            comment: 'Number of offset units (positive=after trigger, negative=before)'
        },

        /**
         * Whether to count in calendar days or business days
         * [C]alendar days or [B]usiness days
         */
        offsetUnit: {
            type: 'char',
            required: true,
            values: ['C', 'B'],
            valueNames: ['Calendar Days', 'Business Days'],
            comment: 'Offset unit: [C]alendar days, [B]usiness days'
        },

        /**
         * If the calculated date falls on a weekend or holiday, which direction to roll
         * [F]orward to next business day or [B]ackward to previous business day
         */
        rolloverDirection: {
            type: 'char',
            values: ['F', 'B', 'W'],
            valueNames: ['Forward', 'Backward', 'Next Weekday'],
            default: 'F',
            comment: 'Rollover direction: [F]orward, [B]ackward, next [W]eekday'
        },

        /**
         * Target weekday for NextWeekday rollover (0=Sun, 1=Mon, ..., 6=Sat).
         * Only used when rolloverDirection is 'W'.
         */
        rolloverWeekday: {
            type: 'int',
            comment: 'Target weekday for Next Weekday rollover (0=Sun, 1=Mon, ..., 6=Sat)'
        },

        /**
         * Additional days for service method extensions (e.g., +5 for mail service)
         */
        serviceExtensionDays: {
            type: 'int',
            comment: 'Extra days for service method extension'
        },

        /**
         * Unit for the service extension: [C]alendar or [B]usiness
         */
        serviceExtensionUnit: {
            type: 'char',
            values: ['C', 'B'],
            valueNames: ['Calendar Days', 'Business Days'],
            comment: 'Service extension unit: [C]alendar, [B]usiness'
        },

        /**
         * Human-readable label for the service extension, e.g. "Mail service +5 days"
         */
        serviceExtensionLabel: {
            type: 'string',
            comment: 'Label describing the service extension'
        },

        /**
         * Priority level for the generated deadline
         */
        priority: {
            type: 'char',
            values: ['L', 'M', 'H'],
            valueNames: ['Low', 'Medium', 'High'],
            default: 'M',
            comment: 'Priority: [L]ow, [M]edium, [H]igh'
        },

        /**
         * Display order within the case type
         */
        sortOrder: {
            type: 'int',
            required: true,
            comment: 'Display order within the case type'
        },

        /**
         * If true, the deadline fires automatically on the trigger event.
         * If false, the user must manually trigger it.
         */
        autoFire: {
            type: 'boolean',
            default: true,
            comment: 'Auto-fire deadline on trigger event?'
        },

        /**
         * Hex color for visual display
         */
        color: {
            type: 'string',
            comment: 'Hex color code for visual display'
        },

        /**
         * Default number of days before the deadline to send a reminder
         */
        reminderDaysBefore: {
            type: 'int',
            comment: 'Days before deadline to send reminder'
        },

        /**
         * If true, this rule applies to all courts (not just assigned ones)
         */
        isCommon: {
            type: 'boolean',
            default: false,
            comment: 'If true, this rule applies to all courts (not just assigned ones)'
        },

        /**
         * If true, count the trigger date as day 1 of the offset
         */
        inclusiveStart: {
            type: 'boolean',
            default: false,
            comment: 'If true, count the trigger date as day 1 of the offset'
        },

        /**
         * If true, the due date is the last day of the deadline period (standard).
         * If false, the period ends before the due date.
         */
        inclusiveEnd: {
            type: 'boolean',
            default: true,
            comment: 'If true, the due date is the last day of the deadline period (standard)'
        }
    },
    indexes: [
        {name: 'notDeleted', fn: s => s.status !== 'D'},
        {name: 'caseCreated', fn: s => s.triggerType === 'C' && s.status !== 'D'},
        {name: 'stageEntered', fn: s => s.triggerType === 'S' && s.status !== 'D'}
    ],
    maps: [
        {
            name: 'caseTypeId',
            keysFn: s => s.caseTypeId,
            presentFn: (s, k) => s.caseTypeId === k && s.status !== 'D'
        },
        {
            name: 'jurisdiction',
            keysFn: s => s.jurisdiction,
            presentFn: (s, k) => s.jurisdiction === k && s.status !== 'D'
        },
        {
            name: 'triggerStageId',
            keysFn: s => s.triggerStageId,
            presentFn: (s, k) => s.triggerStageId === k && s.status !== 'D'
        },
        {
            name: 'triggerRuleId',
            keysFn: s => s.triggerRuleId,
            presentFn: (s, k) => s.triggerRuleId === k && s.status !== 'D'
        }
    ]
});
    }
}
export default DeadlineRule;
